import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";
import { moneyAuto } from "./shared";
import StyleFields from "./StyleFields";

const pad = (n) => String(n).padStart(2, "0");

/** Next 01:00 in the admin's local time, as a datetime-local value. */
function nextOneAm() {
  const d = new Date();
  if (d.getHours() >= 1) d.setDate(d.getDate() + 1);
  d.setHours(1, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Book a run of explanation videos for later.
 * request: { title, summary, cost, items: [{type, id, question_ids?}], mode } or null.
 * With filter ({search, type, subject, difficulty}) instead of items, the run covers
 * every set matching those filters when it is booked.
 */
export default function ScheduleDialog({ request, onClose, onScheduled }) {
  const [runAt, setRunAt] = useState(nextOneAm);
  const [submitting, setSubmitting] = useState(false);
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (request) {
      setRunAt(nextOneAm());
      setStyle({});
    }
  }, [request]);

  const submit = async () => {
    const when = new Date(runAt);
    if (Number.isNaN(when.getTime()) || when <= new Date()) {
      toast.error("Pick a time in the future.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/explanation-videos/schedules", {
        ...(request.filter ? { filter: request.filter } : { items: request.items }),
        mode: request.mode,
        run_at: when.toISOString(),
        ...(Object.keys(style).length ? { style } : {}),
      });
      toast.success(res.data?.message || "Scheduled.");
      onScheduled();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not schedule the videos.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(request)} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{request?.title || "Schedule videos"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          {request?.summary}
          {request?.cost !== undefined && <>, about {moneyAuto(request.cost)}</>}.
        </Typography>
        <TextField
          type="datetime-local"
          label="Run at"
          size="small"
          fullWidth
          value={runAt}
          onChange={(e) => setRunAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Nothing changes until then: existing videos stay as they are. The overnight video check runs at 02:30, so
          a run at 01:00 is checked the same night. You can cancel it from Scheduled runs before it starts.
        </Typography>
        <StyleFields value={style} onChange={setStyle} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : "Schedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
